import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus, Settings } from 'lucide-react';
import { api, label } from '../../lib/api';
import { useStoreEvents } from '../../lib/events';
import { useBase, useScope } from '../../scope';
import { BookingActions, RefusalBox, StatusChip, type BookingTransition } from './actions';
import { refusalOf, todayIn, type Booking, type BookingStatus, type DiningTable, type PlaceBookingInput, type Refusal } from './types';

const STATUSES: { key: BookingStatus | ''; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
];

type WalkIn = { bookingDate: string; bookingTime: string; pax: string; customerName: string; customerPhone: string; customerEmail: string; notes: string; tableId: string };
const blankWalkIn = (date: string): WalkIn => ({ bookingDate: date, bookingTime: '', pax: '2', customerName: '', customerPhone: '', customerEmail: '', notes: '', tableId: '' });

/** The day sheet: one service date, its bookings in time order (the
 * server's order), inline lifecycle actions, and the staff walk-in entry
 * (placed by staff → source pos). Live on the bookings topic. */
export function DaySheet() {
  const { rid, sid, store } = useScope();
  const base = useBase();
  const nav = useNavigate();
  const [picked, setPicked] = useState<string | null>(null);
  const date = picked ?? todayIn(store?.timezone);
  const [status, setStatus] = useState<BookingStatus | ''>('');
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [walkIn, setWalkIn] = useState<WalkIn | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const list = await api.get<Booking[]>(`${base}/bookings`, { date, status: status || undefined });
      if (mine !== seq.current) return;
      setBookings(list);
    } catch (e) {
      if (mine === seq.current) setRefusal(refusalOf(e, 'Failed to load.'));
    }
  }, [base, date, status]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    api.get<DiningTable[]>(`${base}/tables`).then((ts) => setTables(ts.filter((t) => t.active))).catch(() => setTables([]));
  }, [base]);
  useStoreEvents(base, ['bookings'], () => void load());

  async function act(fn: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    try {
      await fn();
      await load();
      setRefusal(null);
      return true;
    } catch (e) {
      setRefusal(refusalOf(e, 'Action failed.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const transition = (bookingId: string, event: BookingTransition, reason?: string) =>
    void act(() => api.post(`${base}/bookings/${bookingId}/${event}`, event === 'reject' || event === 'cancel' ? { reason: reason ?? null } : {}));

  async function placeWalkIn() {
    if (!walkIn) return;
    const body: PlaceBookingInput = {
      bookingDate: walkIn.bookingDate,
      bookingTime: walkIn.bookingTime,
      pax: Number(walkIn.pax) || 0,
      customerName: walkIn.customerName,
      customerPhone: walkIn.customerPhone,
      customerEmail: walkIn.customerEmail.trim() || null,
      notes: walkIn.notes.trim() || null,
      tableId: walkIn.tableId || null,
    };
    const ok = await act(() => api.post<Booking>(`${base}/bookings`, body));
    if (ok) {
      setWalkIn(null);
      if (body.bookingDate !== date) setPicked(body.bookingDate);
    }
  }

  const w = walkIn;
  const set = (patch: Partial<WalkIn>) => w && setWalkIn({ ...w, ...patch });

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Store · Bookings</div>
          <h1>Day sheet</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setWalkIn(w ? null : blankWalkIn(date))}>
            <Plus size={16} /> Walk-in
          </button>
          <Link className="btn btn-outline" to={`/r/${rid}/s/${sid}/bookings/settings`}>
            <Settings size={16} /> Settings
          </Link>
        </div>
      </div>
      <RefusalBox refusal={refusal} />

      <div className="filters" style={{ marginBottom: 18 }}>
        {STATUSES.map((s) => (
          <button key={s.key || 'all'} className={'filter' + (status === s.key ? ' on' : '')} onClick={() => setStatus(s.key)}>
            {s.label}
          </button>
        ))}
        <input className="input" type="date" aria-label="Service date" value={date}
          onChange={(e) => setPicked(e.target.value || null)} style={{ maxWidth: 170, marginLeft: 'auto' }} />
        {picked && <button className="btn btn-ghost btn-sm" onClick={() => setPicked(null)}>Today</button>}
      </div>

      {w && (
        <div className="panel" style={{ marginBottom: 20 }} data-section="walk-in">
          <h2>New walk-in booking</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, paddingTop: 8 }}>
            <div className="field"><label>Date</label>
              <input className="input" type="date" aria-label="Walk-in date" value={w.bookingDate} onChange={(e) => set({ bookingDate: e.target.value })} /></div>
            <div className="field"><label>Time</label>
              <input className="input" type="time" aria-label="Walk-in time" value={w.bookingTime} onChange={(e) => set({ bookingTime: e.target.value })} /></div>
            <div className="field"><label>Guests</label>
              <input className="input" type="number" min={1} aria-label="Walk-in guests" value={w.pax} onChange={(e) => set({ pax: e.target.value })} /></div>
            <div className="field"><label>Name</label>
              <input className="input" placeholder="Guest name" aria-label="Walk-in name" value={w.customerName} onChange={(e) => set({ customerName: e.target.value })} /></div>
            <div className="field"><label>Phone</label>
              <input className="input" placeholder="04…" aria-label="Walk-in phone" value={w.customerPhone} onChange={(e) => set({ customerPhone: e.target.value })} /></div>
            <div className="field"><label>Email</label>
              <input className="input" type="email" placeholder="optional" aria-label="Walk-in email" value={w.customerEmail} onChange={(e) => set({ customerEmail: e.target.value })} /></div>
            <div className="field"><label>Table</label>
              <select className="input" aria-label="Walk-in table" value={w.tableId} onChange={(e) => set({ tableId: e.target.value })}>
                <option value="">No table</option>
                {tables.map((t) => <option key={t.tableId} value={t.tableId}>{t.name}{t.maxGuests ? ` (seats ${t.maxGuests})` : ''}</option>)}
              </select></div>
          </div>
          <div className="field" style={{ marginTop: 12 }}><label>Notes</label>
            <input className="input" placeholder="Highchair, window seat…" aria-label="Walk-in notes" value={w.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => void placeWalkIn()}>{busy ? 'Placing…' : 'Place booking'}</button>
            <button className="btn btn-outline" disabled={busy} onClick={() => setWalkIn(null)}>Discard</button>
          </div>
        </div>
      )}

      {bookings === null && <div className="loading">Loading…</div>}
      {bookings?.length === 0 && <div className="loading">No bookings on {date}.</div>}
      {bookings && bookings.length > 0 && (
        <table className="tbl" data-section="bookings">
          <thead>
            <tr>
              <th>Time</th>
              <th>Guest</th>
              <th className="num">Pax</th>
              <th>Table</th>
              <th>Source</th>
              <th>Status</th>
              <th>Notes</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.bookingId} data-booking={b.bookingId} data-status={b.status} data-source={b.source}
                onClick={() => nav(`/r/${rid}/s/${sid}/bookings/${b.bookingId}`)}>
                <td className="tm">{b.bookingTime}</td>
                <td>
                  <b style={{ fontSize: 14 }}>{b.customerName}</b>
                  <span style={{ color: 'var(--ink-3)', fontSize: 12.5, display: 'block' }}>{b.customerPhoneE164}</span>
                </td>
                <td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.pax}</td>
                <td style={{ color: 'var(--ink-2)' }}>{b.tableName ?? '—'}</td>
                <td><span className="src">{label(b.source)}</span></td>
                <td><StatusChip status={b.status} />{b.statusReason && <span style={{ color: 'var(--ink-3)', fontSize: 12.5, display: 'block' }}>{b.statusReason}</span>}</td>
                <td style={{ color: 'var(--ink-3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.notes ?? ''}</td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <BookingActions status={b.status} busy={busy} small onAction={(ev, reason) => transition(b.bookingId, ev, reason)} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
